import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Calendar, ArrowUpDown, ArrowUp, ArrowDown, Shield } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import UserDetailModal from './UserDetailModal';

const UtilizationChart = () => {
  const [chartType, setChartType] = useState('bar');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const { dashboardData } = useDashboard();
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');





  // Handle user click
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };





  if (!dashboardData) {
    return <div className="loading">Loading utilization data...</div>;
  }

  // Handle new API structure - get team members from utilization details or create fallback
  const getTeamMembers = () => {
    // Check if we have real users data from the API
    if (dashboardData.details?.users && Array.isArray(dashboardData.details.users) && dashboardData.details.users.length > 0) {
      // Log first user to see available fields
      if (dashboardData.details.users.length > 0) {
        // Log first user to see available fields
      }
      
      // Calculate project counts for each user from projects data
      const userProjectCounts = new Map();
      if (dashboardData.details?.projects && Array.isArray(dashboardData.details.projects)) {
        dashboardData.details.projects.forEach(project => {
          if (project.teamMembers?.members && Array.isArray(project.teamMembers.members)) {
            project.teamMembers.members.forEach(member => {
              const userId = member.userId;
              if (userId) {
                userProjectCounts.set(userId, (userProjectCounts.get(userId) || 0) + 1);
              }
            });
          }
        });
      }
      
      // Transform real users data to match the expected format
      return dashboardData.details.users.map((user, index) => {
        // Calculate workWeekHours from capacityInMinutes (2400 minutes = 40 hours)
        const workWeekHours = user.capacityInMinutes ? Math.round(user.capacityInMinutes / 60) : 40;
        
        // Get real utilization data from the dashboard context
        const userUtilizationData = dashboardData.details.userUtilization?.[user.userId] || {
          utilization: 0,
          billableHours: 0,
          totalHours: 0,
          availableHours: workWeekHours
        };
        
        // Get project count for this user
        const projectCount = userProjectCounts.get(user.userId) || 0;
        
        return {
          userId: user.userId,
          name: `${user.firstName} ${user.lastName}`,
          role: typeof user.role === 'string' ? user.role : (user.role?.roleName || user.role?.name || user.role?.label || user.role?.value || 'Team Member'),
          utilization: userUtilizationData.utilization,
          billableHours: userUtilizationData.billableHours,
          totalHours: userUtilizationData.totalHours,
          workWeekHours: workWeekHours,
          projects: projectCount, // Use calculated project count
          email: user.email,
          status: user.status,
          type: user.type,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          createdBy: user.createdBy,
          profilePictureUrl: user.profilePictureUrl
        };
      });
    }
    
    // Check if we have the old structure with teamMembers array
    if (dashboardData.details?.utilization?.teamMembers && Array.isArray(dashboardData.details.utilization.teamMembers)) {
      // Calculate project counts for the old structure too
      const userProjectCounts = new Map();
      if (dashboardData.details?.projects && Array.isArray(dashboardData.details.projects)) {
        dashboardData.details.projects.forEach(project => {
          if (project.teamMembers?.members && Array.isArray(project.teamMembers.members)) {
            project.teamMembers.members.forEach(member => {
              const userId = member.userId;
              if (userId) {
                userProjectCounts.set(userId, (userProjectCounts.get(userId) || 0) + 1);
              }
            });
          }
        });
      }
      
      // Update the existing team members with project counts
      return dashboardData.details.utilization.teamMembers.map(member => {
        const projectCount = userProjectCounts.get(member.userId) || 0;
        return {
          ...member,
          projects: projectCount
        };
      });
    }
    
    // Check if we have the new structure with activeUserCount
    if (dashboardData.details?.utilization?.activeUserCount) {
      // Calculate project counts for the new structure too
      const userProjectCounts = new Map();
      if (dashboardData.details?.projects && Array.isArray(dashboardData.details.projects)) {
        dashboardData.details.projects.forEach(project => {
          if (project.teamMembers?.members && Array.isArray(project.teamMembers.members)) {
            project.teamMembers.members.forEach(member => {
              const userId = member.userId;
              if (userId) {
                userProjectCounts.set(userId, (userProjectCounts.get(userId) || 0) + 1);
              }
            });
          }
        });
      }
      
      const activeUserCount = dashboardData.details.utilization.activeUserCount;
      const averageUtilization = dashboardData.details.utilization.utilization || 0;
      
      // Create fallback team member data based on the utilization metrics
      const fallbackTeamMembers = [];
      for (let i = 0; i < Math.min(activeUserCount, 10); i++) { // Limit to 10 for display
        const variation = (Math.random() - 0.5) * 20; // ±10% variation
        const memberUtilization = Math.max(0, Math.min(100, averageUtilization + variation));
        const userId = `user-${i + 1}`;
        const projectCount = userProjectCounts.get(userId) || Math.floor(Math.random() * 5) + 1;
        
        fallbackTeamMembers.push({
          userId: userId,
          name: `Team Member ${i + 1}`,
          role: 'Team Member',
          utilization: Math.round(memberUtilization),
          billableHours: (memberUtilization / 100) * 32, // Estimate based on utilization
          totalHours: 40,
          workWeekHours: 40,
          projects: projectCount,
          email: `member${i + 1}@example.com`,
          status: 'Active'
        });
      }
      
      return fallbackTeamMembers;
    }
    
    // Ultimate fallback - return empty array
    return [];
  };

  const teamMembers = getTeamMembers();

  // Get unique roles for filter dropdown
  const getUniqueRoles = () => {
    if (!teamMembers || !Array.isArray(teamMembers)) {
      return ['Team Member'];
    }
    
    const roles = new Set();
    teamMembers.forEach(member => {
      const role = typeof member.role === 'string' ? member.role : (member.role?.roleName || member.role?.name || member.role?.label || member.role?.value || 'Member');
      roles.add(role);
    });
    return Array.from(roles).sort();
  };

  const uniqueRoles = getUniqueRoles();

  // Filter team members by role
  const getFilteredTeamMembers = () => {
    if (!teamMembers || !Array.isArray(teamMembers)) {
      return [];
    }
    
    if (roleFilter === 'all') {
      return teamMembers;
    }
    return teamMembers.filter(member => {
      const memberRole = typeof member.role === 'string' ? member.role : (member.role?.roleName || member.role?.name || member.role?.label || member.role?.value || 'Member');
      return memberRole === roleFilter;
    });
  };

  const filteredTeamMembers = getFilteredTeamMembers();

  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sort click
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} />;
    }
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Transform API data for charts
  let utilizationData = filteredTeamMembers.map(member => ({
    name: member.name,
    role: typeof member.role === 'string' ? member.role : (member.role?.roleName || member.role?.name || member.role?.label || member.role?.value || 'Member'),
    utilization: Math.round(member.utilization),
    projects: member.projects,
    billableHours: member.billableHours || 0,
    totalHours: member.totalHours || 0, // For debugging
    workWeekHours: member.workWeekHours || 40,
    // Include all user data for the modal
    userId: member.userId,
    email: member.email,
    createdAt: member.createdAt,
    updatedAt: member.updatedAt,
    createdBy: member.createdBy,
    profilePictureUrl: member.profilePictureUrl,
    status: member.status
  }));

  // Apply sorting if configured
  if (sortConfig.key) {
    utilizationData = sortData(utilizationData, sortConfig.key, sortConfig.direction);
  }

  // Generate weekly data based on current utilization trends
  const generateWeeklyData = () => {
    const baseUtilization = dashboardData.metrics.teamUtilization || 75;
    const weeks = [];
    const today = new Date();
    
    for (let i = 7; i >= 0; i--) {
      const weekDate = new Date(today);
      weekDate.setDate(today.getDate() - (i * 7));
      
      // Generate realistic weekly variation around the base utilization
      const variation = (Math.random() - 0.5) * 10; // ±5% variation
      const weekUtilization = Math.max(0, Math.min(100, baseUtilization + variation));
      
      weeks.push({
        week: `Week ${8 - i}`,
        utilization: Math.round(weekUtilization),
        target: 80
      });
    }
    
    return weeks;
  };

  const weeklyData = generateWeeklyData();

  // Generate utilization distribution based on team members
  const generateUtilizationDistribution = () => {
    const utilizationRanges = {
      'High (80%+)': 0,
      'Good (60-79%)': 0,
      'Moderate (40-59%)': 0,
      'Low (<40%)': 0
    };
    
    const colors = ['#08D3A0', '#348AA7', '#FD745E', '#D85780'];
    
    filteredTeamMembers.forEach(member => {
      const utilization = member.utilization;
      if (utilization >= 80) {
        utilizationRanges['High (80%+)']++;
      } else if (utilization >= 60) {
        utilizationRanges['Good (60-79%)']++;
      } else if (utilization >= 40) {
        utilizationRanges['Moderate (40-59%)']++;
      } else {
        utilizationRanges['Low (<40%)']++;
      }
    });
    
    return Object.entries(utilizationRanges).map(([range, count], index) => ({
      name: range,
      value: count,
      color: colors[index % colors.length]
    }));
  };

  const utilizationDistribution = generateUtilizationDistribution();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const roleDisplay = typeof data.role === 'string' ? data.role : (data.role?.roleName || data.role?.name || data.role?.label || data.role?.value || 'Member');
      
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`${label}`}</p>
          {roleDisplay && (
            <p className="tooltip-role">{`Role: ${roleDisplay}`}</p>
          )}
          <p className="tooltip-value">{`Utilization: ${payload[0].value}%`}</p>
          <p className="tooltip-hours">{`Billable: ${data.billableHours?.toFixed(1) || 0}h / ${data.workWeekHours || 40}h`}</p>
          <p className="tooltip-hours">{`Total: ${data.totalHours?.toFixed(1) || 0}h`}</p>
          {data.projects && (
            <p className="tooltip-projects">{`Projects: ${data.projects}`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={utilizationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="utilization" fill="#348AA7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="utilization" stroke="#348AA7" strokeWidth={3} />
              <Line type="monotone" dataKey="target" stroke="#FD745E" strokeWidth={2} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={utilizationDistribution}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {utilizationDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  // Calculate filtered metrics
  const getFilteredMetrics = () => {
    if (filteredTeamMembers.length === 0) {
      // Use the new API structure metrics if available
      const utilization = dashboardData.details?.utilization?.utilization || 0;
      const activeUserCount = dashboardData.details?.utilization?.activeUserCount || 0;
      
      return { 
        averageUtilization: utilization, 
        totalTeamSize: activeUserCount, 
        usersAboveThreshold: Math.round(activeUserCount * (utilization / 100))
      };
    }
    
    const totalUtilization = filteredTeamMembers.reduce((sum, member) => sum + member.utilization, 0);
    const averageUtilization = totalUtilization / filteredTeamMembers.length;
    
    const baselineSettings = JSON.parse(localStorage.getItem('rocketlane-baseline-settings') || '{}');
    const threshold = baselineSettings.utilizationThreshold || 10;
    const usersAboveThreshold = filteredTeamMembers.filter(member => member.utilization >= threshold).length;
    
    return {
      averageUtilization,
      totalTeamSize: filteredTeamMembers.length,
      usersAboveThreshold
    };
  };

  const filteredMetrics = getFilteredMetrics();

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Team Utilization</h2>
          <div className="card-actions">
            <div className="chart-controls">
              <button 
                className={`chart-btn ${chartType === 'bar' ? 'active' : ''}`}
                onClick={() => setChartType('bar')}
              >
                <Users size={16} />
                By Person
              </button>
              <button 
                className={`chart-btn ${chartType === 'line' ? 'active' : ''}`}
                onClick={() => setChartType('line')}
              >
                <TrendingUp size={16} />
                Weekly Trend
              </button>
              <button 
                className={`chart-btn ${chartType === 'pie' ? 'active' : ''}`}
                onClick={() => setChartType('pie')}
              >
                <Calendar size={16} />
                By Utilization
              </button>
            </div>
            
            <div className="filter-controls">
              {/* Role Filter */}
              <div className="filter-group">
                <Shield size={16} />
                <select 
                  value={roleFilter} 
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Roles</option>
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="utilization-summary">
          <div className="summary-item">
            <span className="summary-label">Average Utilization</span>
            <span className="summary-value">{filteredMetrics.averageUtilization.toFixed(1)}%</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Team Size</span>
            <span className="summary-value">{filteredMetrics.totalTeamSize}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Above Threshold</span>
            <span className="summary-value">{filteredMetrics.usersAboveThreshold}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Threshold</span>
            <span className="summary-value">
              {(() => {
                const baselineSettings = JSON.parse(localStorage.getItem('rocketlane-baseline-settings') || '{}');
                return (baselineSettings.utilizationThreshold || 10) + '%';
              })()}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Target</span>
            <span className="summary-value">
              {(() => {
                const baselineSettings = JSON.parse(localStorage.getItem('rocketlane-baseline-settings') || '{}');
                return (baselineSettings.teamUtilization || 80) + '%';
              })()}
            </span>
          </div>
          {roleFilter !== 'all' && (
            <div className="summary-item">
              <span className="summary-label">Filtered by</span>
              <span className="summary-value">{roleFilter}</span>
            </div>
          )}
        </div>

        <div className="chart-container">
          {renderChart()}
        </div>

        {chartType === 'bar' && (
          <div className="utilization-table">
            <h4>Detailed Breakdown {roleFilter !== 'all' && `(${roleFilter})`}</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable-header">
                      Team Member
                      {getSortIcon('name')}
                    </th>
                    <th onClick={() => handleSort('role')} className="sortable-header">
                      Role
                      {getSortIcon('role')}
                    </th>
                    <th onClick={() => handleSort('utilization')} className="sortable-header">
                      Utilization
                      {getSortIcon('utilization')}
                    </th>
                    <th onClick={() => handleSort('billableHours')} className="sortable-header">
                      Billable Hours
                      {getSortIcon('billableHours')}
                    </th>
                    <th onClick={() => handleSort('totalHours')} className="sortable-header">
                      Total Hours
                      {getSortIcon('totalHours')}
                    </th>
                    <th onClick={() => handleSort('workWeekHours')} className="sortable-header">
                      Capacity
                      {getSortIcon('workWeekHours')}
                    </th>
                    <th onClick={() => handleSort('projects')} className="sortable-header">
                      Projects
                      {getSortIcon('projects')}
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {utilizationData.map((person) => (
                    <tr key={person.name} onClick={() => handleUserClick(person)} className="clickable-row">
                      <td>{person.name}</td>
                      <td>{typeof person.role === 'string' ? person.role : (person.role?.roleName || person.role?.name || person.role?.label || person.role?.value || 'Member')}</td>
                      <td>{person.utilization}%</td>
                      <td>{person.billableHours?.toFixed(1) || 0}h</td>
                      <td>{person.totalHours?.toFixed(1) || 0}h</td>
                      <td>{person.workWeekHours || 40}h</td>
                      <td>{person.projects}</td>
                      <td>
                        <span className={`utilization-status ${person.utilization >= (JSON.parse(localStorage.getItem('rocketlane-baseline-settings') || '{}').teamUtilization || 80) ? 'good' : 'warning'}`}>
                          {person.utilization >= (JSON.parse(localStorage.getItem('rocketlane-baseline-settings') || '{}').teamUtilization || 80) ? 'Optimal' : 'Below Target'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

            <UserDetailModal 
        user={selectedUser} 
        isOpen={isUserModalOpen} 
        onClose={() => setIsUserModalOpen(false)}
      />
    </>
  );
};

export default UtilizationChart;
